// eslint-disable-next-line import-x/no-extraneous-dependencies
import { Time } from '@internationalized/date';
import { format } from 'date-fns';

export const ONE_SECOND = 1000;
export const ONE_HOUR_IN_SECONDS = 3600;
export const ONE_DAY_IN_SECONDS = ONE_HOUR_IN_SECONDS * 24;
export const ONE_MINUTE_IN_SECONDS = 60;
export const TIME_FORMAT_24_HOURS = 'HH:mm';
export const TIME_FORMAT_12_HOURS = 'hh:mm a';

export type TimeFormat = typeof TIME_FORMAT_24_HOURS | typeof TIME_FORMAT_12_HOURS;

export const convertTimeFromNumberToStringValue = (
  timeInSeconds: number,
  localeObj?: Locale,
  useAmPmFormat = false
) => {
  const date = new Date(0);
  const dateFormat = useAmPmFormat ? TIME_FORMAT_12_HOURS : TIME_FORMAT_24_HOURS;

  const hours = Math.floor(timeInSeconds / ONE_HOUR_IN_SECONDS);
  const minutes = (timeInSeconds - hours * ONE_HOUR_IN_SECONDS) / ONE_MINUTE_IN_SECONDS;

  date.setHours(hours);
  date.setMinutes(minutes);

  return format(date, dateFormat, { locale: localeObj });
};

export const isAmPmFormat = (time: string) => time.toUpperCase().match('(AM|PM)');

export const convertTimeFromStringToNumberValue = (time: string) => {
  const parsedTime = time.match(/(\d\d):(\d\d)/);

  if (!parsedTime) {
    return -1;
  }

  const isAmPm = isAmPmFormat(time);
  const minutes = Number.parseInt(parsedTime[2], 10);
  let hours = Number.parseInt(parsedTime[1], 10);

  if (isAmPm) {
    if (hours === 12) {
      hours = 0;
    }

    if (isAmPm[1] === 'PM') {
      hours += 12;
    }
  }

  return hours * ONE_HOUR_IN_SECONDS + minutes * ONE_MINUTE_IN_SECONDS;
};

export const convertTimeFrom24to12HoursFormat = (time: string, localeObj?: Locale) => {
  const timeAsNumber = convertTimeFromStringToNumberValue(time);

  return convertTimeFromNumberToStringValue(timeAsNumber, localeObj, true);
};

export const formatTimeToTwoDigitString = (value: number) => `${value < 10 ? '0' : ''}${value}`;

export const identifyTimeFormat = (locale: string) => {
  const time = new Date().toLocaleTimeString(locale);

  if (time.toLowerCase().match(/am|pm/)) {
    // system uses 12 hours format
    return TIME_FORMAT_12_HOURS;
  }

  // system uses 24 hours format
  return TIME_FORMAT_24_HOURS;
};

export const toSecondsFromTime = (time: Time) => time.hour * 60 * 60 + time.minute * 60 + time.second;

export const toTimeFromSeconds = (seconds: number) => new Time().add({ seconds });
